import { useState, useEffect, useCallback } from "react";
import PostComposer from "../components/PostComposer";
import PostCard from "../components/PostCard";
import ReportDialog from "../components/ReportDialog";
import api from "../lib/api";
import { Heart, Users, Globe, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import RecapModal from "../components/RecapModal";

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [scope, setScope] = useState("all");
  const [recapOpen, setRecapOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/posts/feed?scope=${scope}`);
    const seen = new Set();
    setPosts(data.filter((p) => (seen.has(p.post_id) ? false : seen.add(p.post_id))));
    setLoading(false);
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-copper flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Community Timeline</p>
        <h1 className="font-serif text-3xl font-bold text-bronze">What's happening</h1>
      </div>
      <button data-testid="recap-banner" onClick={() => setRecapOpen(true)}
        className="w-full mb-5 text-left rounded-2xl p-4 flex items-center gap-3 theme-copper text-white shadow-md hover:shadow-lg transition-shadow relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 paper-bg" />
        <Sparkles className="w-7 h-7 shrink-0 relative" />
        <div className="relative flex-1">
          <p className="font-serif font-semibold text-lg leading-tight">Your Weekly Recap is ready ✨</p>
          <p className="text-white/90 text-sm">See the good you were part of this week.</p>
        </div>
        <span className="relative text-sm font-semibold bg-white/20 rounded-full px-3 py-1">Open</span>
      </button>
      <div className="flex gap-2 mb-5">
        <Button data-testid="feed-scope-all" onClick={() => setScope("all")} variant={scope === "all" ? "default" : "outline"}
          className={`rounded-full ${scope === "all" ? "bg-copper hover:bg-copper/90 text-white" : ""}`}><Globe className="w-4 h-4 mr-1.5" /> Everyone</Button>
        <Button data-testid="feed-scope-following" onClick={() => setScope("following")} variant={scope === "following" ? "default" : "outline"}
          className={`rounded-full ${scope === "following" ? "bg-copper hover:bg-copper/90 text-white" : ""}`}><Users className="w-4 h-4 mr-1.5" /> Following</Button>
      </div>
      <div className="mb-6"><PostComposer onPosted={(p) => setPosts((prev) => (prev.some((x) => x.post_id === p.post_id) ? prev : [p, ...prev]))} /></div>
      {loading ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-secondary animate-pulse" />)}</div>
      ) : posts.length === 0 ? (
        <p className="text-center text-muted-foreground py-16">{scope === "following" ? "You're not following anyone yet. Visit a profile and tap Follow to see their updates here 💛" : "No posts yet. Be the first to share something 💛"}</p>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => <PostCard key={p.post_id} post={p} onChange={load} onReport={setReport} />)}
        </div>
      )}
      <ReportDialog target={report} onClose={() => setReport(null)} />
      <RecapModal open={recapOpen} onClose={() => setRecapOpen(false)} />
    </div>
  );
}
